import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, Renderer2 } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { OidcSecurityService,   PublicEventsService, EventTypes, OidcClientNotification, AuthStateResult, AuthenticatedResult, } from 'angular-auth-oidc-client'; //'@core/auth/angular-auth-oidc-client'; 
import { BreakpointObserver } from '@angular/cdk/layout';

import { map, filter, take, tap, switchMap, combineLatestWith } from 'rxjs/operators';
import * as fromRoot from '../../../reducers';
import { UserInfo, Notification, MeteringPoint, MenuItem, ApplicationConfiguration, BannerMessage, Tip } from '@model';
import * as fromAccountStore from '@appStore/account';
import * as fromHelpStore from '@appStore/help';
import * as fromConfigurationStore from '@appStore/configuration';
import * as fromCombinedSelectors from '@appStore/account/combined-selectors';
import * as fromMeteringStore from '@appStore/metering-points';
import * as fromInformationStore from '@core/store';

import { MatSidenav } from '@angular/material/sidenav';
import { HelpState, UserType, TypeOfSystemMessage } from '@enums';
import { Router, NavigationEnd } from '@angular/router';
import { SxMonitoringService } from '../../../services/sx-monitoring.service';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, APP_ID, Inject } from '@angular/core';
import { SubSink } from 'subsink';
import { SxNavigationMenuService, SxCookieService, SxErrorService } from '@services';
import { NotFoundPageComponent } from '..';
import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';
import { environment } from '@environment';
import * as fromCustomersStore from '@appStore/customers';
import * as fromAppStore from '@appStore/app';
import { _ } from '@ngx-translate/core';

@Component({
    selector: 'app-root',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('drawer')
  sideNav: MatSidenav;

  isLoggedInAndPermissionsLoaded$: Observable<boolean>;
  isPermissionLoading$: Observable<boolean>;
  unreadNotificationCount$: Observable<number>;
  unreadNotifications$: Observable<Notification[]>;
  unreadMessagesCount$: Observable<number>;
  bannerMessages$: Observable<BannerMessage[]>;
  userInfo$: Observable<UserInfo>;
  meteringPoints$: Observable<{ meteringPoints: MeteringPoint[]; loaded: boolean }>;
  isMenuItemsLoaded$: Observable<boolean>;
  menuItems: MenuItem[];
  displayPageChrome = false;
  showMultiLevelMenu = false;
  isImpersonating = false;
  crmUsermatchingFormSubmited = false;
  isEnableAppLauncher = false;

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe('(max-width: 992px)')
    .pipe(map((result) => result.matches));

  isMobile$: Observable<boolean> = this.breakpointObserver
    .observe('(max-width: 576px)')
    .pipe(map((result) => result.matches));

  currentHelpState$: Observable<HelpState>;
  isShowTips$: Observable<boolean>;
  isAuthorized = false;
  clientId: string = "";
  inActiveHelp = HelpState.closed;
  appRouter: Router;
  tips$: Observable<Tip[]>;
  unreadTipsCount$: Observable<number>;

  inActiveFilter = true;

  userType$: Observable<UserType>;
  consentApproved = false;

  private subs = new SubSink();
  private webjobHubConnection: HubConnection;
  private agreementHubConnection: HubConnection;

  isWhiteLabel: boolean;
  isOnboarding: boolean;
  isOnboardingComplete: boolean;
  settings: ApplicationConfiguration;
  userId: string;
  userEmail: string = "";
  userFullName: string = "";

  loadSubmitForm: boolean;
  traceSubmitted: any;
  noCustomerRegistered = false;

  private isMobile = false;
  private isSidebarActive$: Observable<boolean>;
  private isSidebarActive = false;
  
  constructor(
    private store: Store<fromRoot.State>,
    private accountStore: Store<fromAccountStore.State>,
    private helpStore: Store<fromHelpStore.State>,
    private meteringPointStore: Store<fromMeteringStore.State>,
    private informationStore: Store<fromInformationStore.InformationState>,
    private appStore: Store<fromAppStore.State>,
    private oidcSecurityService: OidcSecurityService,
    private oidcEventService: PublicEventsService,
    private breakpointObserver: BreakpointObserver,
    private sxCookieService: SxCookieService,
    private router: Router,
    private monitoringService: SxMonitoringService,
    public navigationMenuService: SxNavigationMenuService,
    private renderer2: Renderer2,
    private customerStore: Store<fromCustomersStore.CustomersState>,
    private sxErrorService: SxErrorService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(APP_ID) private appId: string
  ) {
    this.appRouter = router;
  }

  ngOnInit() {
    this.isLoggedInAndPermissionsLoaded$ = this.accountStore.select(fromCombinedSelectors.getIsAuthorizedAndPermissionsLoaded);
    this.isPermissionLoading$ = this.store.select(fromConfigurationStore.getPermissionsLoading);
    this.unreadNotificationCount$ = this.accountStore.select(fromAccountStore.getUnreadNotificationsCount);
    this.unreadMessagesCount$ = this.accountStore.select(fromAccountStore.getUnreadMessagesCount);
    this.userInfo$ = this.accountStore.select(fromAccountStore.getUserInfo);
    this.unreadNotifications$ = this.accountStore.select(fromAccountStore.getAllUnreadNotifications);
    this.currentHelpState$ = this.helpStore.select(fromHelpStore.getCurrentHelpState);
    this.subs.sink = this.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
      this.showOrHideCatbotAndHotjar();
    });

    this.userType$ = this.accountStore.select(fromAccountStore.getUserType);
    this.meteringPoints$ = this.meteringPointStore.select(fromMeteringStore.getMeteringPointsAndLoaded);
    this.isMenuItemsLoaded$ = this.store.select(fromConfigurationStore.getMenuConfigurationLoaded);
    this.bannerMessages$ = this.informationStore.select(fromInformationStore.getBannerMessages);
    this.isShowTips$ = this.appStore.select(fromAppStore.getIsTipWindowOpened);

    this.tips$ = this.appStore.select(fromAppStore.getTips);
    this.unreadTipsCount$ = this.appStore.select(fromAppStore.getUnreadTipsCount);
    this.isSidebarActive$ = this.appStore.select(fromAppStore.getAnyActiveSidbars);
    this.subs.sink = this.isSidebarActive$.subscribe(isSidebarActive => {
      this.isSidebarActive = isSidebarActive;
      this.showOrHideCatbotAndHotjar();
    });

    this.store.dispatch(new fromConfigurationStore.SystemContentLoad);
    this.subs.sink = this.customerStore.select(fromCustomersStore.getCustomersLoaded)
    .pipe(
      filter(x => !!x),
      take(1)
    ).subscribe(() =>{
      this.subs.sink = this.customerStore.select(fromCustomersStore.getCustomers)
      .subscribe(customers => {
        let deeplink = this.sxCookieService.getCookie('SX_DEEPLINK');
        
        if (!customers || customers.length === 0) {
          this.noCustomerRegistered = true;
        } else if (customers) {
          // Ändrad så att om ALLA kunder är white label så sätts isWhiteLabel till true
          this.isWhiteLabel = customers.every((c) => c.isWhiteLabel);
        }

        // Only use deeplink if onbaording is completed
        if (deeplink && this.isOnboardingComplete) {
          //console.log('deeplink: ' + deeplink + ' && this.isOnboardingComplete: ' + this.isOnboardingComplete );
          this.sxCookieService.clearCookie('SX_DEEPLINK');

          //console.log('noCustomerRegistered: ' + this.noCustomerRegistered );
          if (!this.noCustomerRegistered) {
            // Only navigate to deeplink-url if user has customers
            this.router.navigate([deeplink]);
          }
        }
      });
    });

    this.monitoringService.userType$ = this.userType$;

    this.subs.sink = this.store
      .select(fromConfigurationStore.getApiConfiguration)
      .pipe(
        filter((x) => !!x),
        take(1)
      )
      .subscribe((apiConfig) => {
        this.showMultiLevelMenu = apiConfig.menu_show_multilevel;
        this.isEnableAppLauncher = apiConfig.header_enable_app_launcher;
        this.settings = apiConfig;
        // tslint:disable-next-line:max-line-length
        this.changeDetectorRef.detectChanges(); // using Safe Navigation Operator on alice-settings require detection of changes when in Preview (not logged in)
      });
      
    this.subs.sink = this.store.select(fromConfigurationStore.getSystemContentLoaded)
      .pipe(
        filter(x => !!x),
        take(1),
        switchMap(_ => this.store.select(fromConfigurationStore.getSystemContent))
      ).subscribe(content => {
        if(content?.preClosureMessage){
          let severity = content.typeOfPreClosureMessage == TypeOfSystemMessage.Information ?  1 : content.typeOfPreClosureMessage == TypeOfSystemMessage.Warning ? 2 : 3;
          this.informationStore.dispatch(new fromInformationStore.AddPreClosureBanner(
            { 
            id: -1, 
            isVisible: content.typeOfPreClosureMessage !== TypeOfSystemMessage.MessageOff, 
            content: content.preClosureMessage, 
            icon: content.preClosureMessageIcon,
            severity:  severity 
            }
          ));
        }
      });
     
      /*
    this.subs.sink = this.store.select(fromConfigurationStore.getIsAuthSetup)
      .pipe(
        filter(isSetup => isSetup),
        take(1),
        switchMap(() => this.store.select(fromConfigurationStore.getActiveClientId)),
        filter(clientId => !!clientId),
        take(1),
        switchMap(clientId => this.oidcSecurityService.checkAuth(null, clientId))
      ).subscribe(result => {
        console.log(`app-component\tcheckAuth`, result.isAuthenticated);
      });
      */

    
    this.subs.sink = this.oidcSecurityService.checkAuthMultiple()
      .subscribe(result => {
        //console.log(`app-component\tcheckAuth`, result);
      });
    
    // ON AUTHED (AT LOGIN AND SILENT RENEW)
    this.subs.sink = this.oidcEventService.registerForEvents()
        .pipe(
          filter(notification => notification.type == EventTypes.NewAuthenticationResult )
        ).subscribe((result: OidcClientNotification<AuthStateResult>) => {
          //console.log(`app-component\tNewAuthenticationResult`, result);
          // handle un-authorized
          if (!result?.value?.isAuthenticated){
            if (window.parent) {
              window.parent.location.href = '/unauthorized';
            } else {
              window.location.href = '/unauthorized';
            }
            return;
          }
        });

    // Hanldle initial authentication
    this.oidcSecurityService.isAuthenticated$
        .pipe(
          filter(authResult => !!authResult && !!authResult.allConfigsAuthenticated && authResult.allConfigsAuthenticated.length > 0),
          take(1)
        ).subscribe(authResult => {
          //console.log(`app-component\tisAuthenticated$\tauthResult`,authResult);
          let authenticatedConfig = authResult.allConfigsAuthenticated.find(x => x.isAuthenticated);
          if (!authenticatedConfig)
            return;
          //console.log(`app-component\tisAuthenticated$\tauthenticatedConfig`, authenticatedConfig);
          this.isAuthorized = authenticatedConfig.isAuthenticated;
          this.store.dispatch(new fromConfigurationStore.LoadPermission());
          this.accountStore.dispatch(new fromAccountStore.UserInfoLoad());
        });

    // Handle user data from OIDC (is automatically called when user is authenticated - see auth-config.module.ts)
    this.subs.sink = this.oidcSecurityService.userData$
      .pipe(filter(userDataResult => !!userDataResult && !!userDataResult.allUserData && userDataResult.allUserData.length > 0))
      .subscribe(userDataResult => {
        //console.log(`app-component\tuserData`, userDataResult);
        let authenticatedConfig = userDataResult.allUserData.find(x => !!x.userData);
        if (!authenticatedConfig)
          return;
        //console.log(`app-component\tuserData$\tauthenticatedConfig`, authenticatedConfig);
        
        let userInfo: UserInfo = this.getUserInfoFromOidc(authenticatedConfig.userData);
        //console.log(`app-component\tuserData\tuserInfo`, userInfo);
        if (userInfo) {
          if (userInfo.impersonatedAsName) {
            window.document.title = 'Stockholm Exergi - ' + userInfo.impersonatedAsName + ' (Imiterar)';
          }
        }
        this.isOnboardingComplete = userInfo.onBoardingComplete;
        this.isImpersonating = !!userInfo.impersonatedAsName;
        this.accountStore.dispatch(new fromAccountStore.UserInfoLoadSuccess(userInfo));
        this.userId = userInfo.id;
        this.userEmail = userInfo.email;
        this.userFullName = userInfo.name;
        this.monitoringService.setAuthenticatedUserId(userInfo.id);
        this.loadTimedData();
        // Set Cookie Consent approved by default when authorized if user is not direct-accessing via Segment
        if (!localStorage.getItem('CONSENT') && !localStorage.getItem('SHOW_CONSENT')) {
          localStorage.setItem('CONSENT', 'YES');
          this.consentApproved = localStorage.getItem('CONSENT') === 'YES';
          localStorage.removeItem('SHOW_CONSENT');
          this.changeDetectorRef.detectChanges();
        }
        this.appStore.select(fromAppStore.getIsTipsLoaded)
        .pipe(
            filter(loaded => !loaded),
            take(1)
        ).subscribe(() => {
            this.appStore.dispatch(new fromAppStore.TipsLoad());
        });
        if (this.settings.crmFormIntegrationEnabled) {
          this.loadCrmIntegrationScripts();
        }
        if (this.settings.crmChatbotEnabled){
          this.loadCrmChatbotScript();
          setTimeout(() => {
            this.showOrHideCatbotAndHotjar();
            //console.log(`app\tlcrmChatbotEnabledt\ttimeout`);
          }, 500);
        }
        if (!this.traceSubmitted) {
          this.monitoringService.logEvent('User logged in', {});
          this.traceSubmitted = true;
        }
    });

    // Get Menu configuration
    this.subs.sink = this.store
      .select(fromConfigurationStore.getMenuConfigurationLoaded)
      .pipe(
        filter((x) => !!x),
        take(1),
        switchMap((_) => this.store.select(fromConfigurationStore.getMenuConfiguration))
      )
      .subscribe((items) => {
        this.menuItems = items;
        // Build routes from navigation definition
        this.navigationMenuService.addEpiContentNodesToRouting(this.router, this.menuItems);

        let routerConfig = this.router.config;

        // Method: Add failover route last
        routerConfig.push({ path: '**', component: NotFoundPageComponent });

        this.router.resetConfig(routerConfig);
      });

    this.subs.sink = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.displayPageChrome = !(
          (event.url.includes('/secure/register') && !event.url.endsWith('additional')) ||
          event.url.includes('/error') ||
          event.url.includes('/loading-report') ||
          event.url.includes('/onboarding') ||
          event.url.includes('/unauthorized')
        );
        this.isOnboarding =
          event.url.includes('/verifyemail') ||
          event.url.includes('/secure/user/onboardinginfo') ||
          (event.url.includes('/secure/register') && !event.url.endsWith('/additional'));
      });

    this.setupSignalR();
  }

  loadCrmIntegrationScripts() {
      setTimeout(() => {

        try {
        let formLoaderScript = this.renderer2.createElement('script');
        formLoaderScript.type = `text/javascript`;
        formLoaderScript.id = `formScript`;
        formLoaderScript.src = this.settings.crmFormLoaderScriptPath;

        let wsTrackingScript = this.renderer2.createElement('script');
        wsTrackingScript.type = `text/javascript`;
        wsTrackingScript.id = `trackingScript`;
        wsTrackingScript.src = this.settings.crmFormTradckingScriptPath;


        this.renderer2.appendChild(document.head, formLoaderScript);
        this.renderer2.appendChild(document.head, wsTrackingScript);
        
        const FORM_PAGE_ID_JS_LIBRARY = this.settings.crmFormPageIdJsLibrary;
        const FORM_PAGE_ID_USER_SUBMIT = this.settings.crmFormPageIdUserSubmit;
        const FORM_FIELD_ID_USERID = this.settings.crmFormFieldIdUserId;
        const FORM_FIELD_ID_LOGINDATE = this.settings.crmFormFieldIdLoginDate;

        // Logic after form loaded
        let checkExist = setInterval(() => {

          if (window['MsCrmMkt']) {

            try {

              let MsCrmMkt = window['MsCrmMkt'];
 
              MsCrmMkt.MsCrmFormLoader.on('afterFormLoad', (event) => {
                let frmPageId = event.getFormPageId();

                if (frmPageId === FORM_PAGE_ID_JS_LIBRARY) {
                  // JS library form loader
                  // Enable tracking right after the JS library form is loaded

                  let Alice = window['Alice'];

                  Alice.Marketing.ContentManager.enableMktTracking();

                  setTimeout(() => {
                    this.loadSubmitForm = true;

                    setTimeout(() => {
                      let _Alice = window['Alice'];
                     _Alice.Marketing.ContentManager.reloadMarketingForms();
                  }, 1000);
                }, 1000);

                // Dynamically load the user-submit form
                this.crmUsermatchingFormSubmited = true;

                let frmPageId = event.getFormPageId();

                // Initialize Marketing user tracking and content render logic

                // Set empty guid if user is impersonation
                let userId = '00000000-0000-0000-0000-000000000000';

                if (!this.isImpersonating) {
                  userId = this.userId;
                }

                let metaDataOptions = {
                  // Value from "data-form-block-id"
                  userMatchingMktFormPageGuid: FORM_PAGE_ID_USER_SUBMIT,
                  jsLibraryMktFormPageGuid: FORM_PAGE_ID_JS_LIBRARY,
                  // d value from form page field
                  userIdFieldGuid: FORM_FIELD_ID_USERID,
                  userLoginDateFieldGuid: FORM_FIELD_ID_LOGINDATE
                };
                let userDataOptions = {
                  userIdGuid: userId,
                  userLoginDateTime: new Date()
                };
                // Core initialization function
                Alice.Marketing.ContentManager.initializeUserTracking(metaDataOptions, userDataOptions);
              }
              });
              clearInterval(checkExist);

            } catch (error) {
              // cLog to Application Insight
              this.sxErrorService.logError(error);
              clearInterval(checkExist); // Clear interval, otherwise same error will be repetedly logged
            }
          }
        }, 100); // check every 100ms
      } catch (err) {
        // Log to Application Insight
        this.sxErrorService.logError(err);
      }
    }, 1);
  }

  loadCrmChatbotScript(){
    setTimeout(() => {
      //console.log(`app\tloadCrmChatbotScript`, this.settings);
        
      let chatbotScript = this.renderer2.createElement('script') as HTMLScriptElement;
      chatbotScript.type = "text/javascript";
      chatbotScript.id = "Microsoft_Omnichannel_LCWidget";
      chatbotScript.src = this.settings.crmChatbotScriptSrc; 
      chatbotScript.setAttribute("v2", '');
      chatbotScript.setAttribute('data-app-id', this.settings.crmChatbotAppId); 
      chatbotScript.setAttribute('data-lcw-version', 'prod');
      chatbotScript.setAttribute('data-org-id', this.settings.crmChatbotOrgId); 
      chatbotScript.setAttribute('data-org-url', this.settings.crmChatbotOrgUrl); 
      chatbotScript.setAttribute('data-customization-callback', this.settings.crmChatbotCustomizationCallback);
      chatbotScript.setAttribute('data-color-override', this.settings.crmChatbotColorOverride);
      chatbotScript.setAttribute('data-suggested-action-layout', this.settings.crmChatbotSuggestedActionLayout);
      chatbotScript.setAttribute('data-font-family-override', this.settings.crmChatbotFontFamilyOverride);
      chatbotScript.setAttribute('data-enable-sso-magic-code', this.settings.crmChatbotEnableSsoMagicCode);
      this.renderer2.appendChild(document.head, chatbotScript);
  
      window.addEventListener("lcw:ready", ()=>{
        try{
          let customContext = { 
            'MSPUserId': { value: this.userId, isDisplayable: true },
            'Email': { value: this.userEmail, isDisplayable: true},
            'MSPFullName': { value: this.userFullName, isDisplayable: true }
          };

          //(<any>window).Microsoft.Omnichannel.LiveChatWidget.SDK.removeContextProvider();
          (<any>window).Microsoft.Omnichannel.LiveChatWidget.SDK.setContextProvider(()=>{
            return customContext;
          });
        }
        catch(error){
          this.sxErrorService.logError(error);            
        }
      });
    }, 1);
  }

  showOrHideCatbotAndHotjar(){
    let shouldHideHotjar = this.isSidebarActive;
    let shouldHideChatbot = this.isSidebarActive || this.isMobile;

    if (this.settings?.crmChatbotElementId){
      let chatbotIframe = document.getElementById(this.settings.crmChatbotElementId) as HTMLIFrameElement;
      if (!!chatbotIframe){
        chatbotIframe.hidden = shouldHideChatbot;
      }
      this.changeDetectorRef.detectChanges();
    }

    if (this.settings?.hotjarWidgetClassName){
      let hotjarWidget = document.getElementsByClassName(this.settings.hotjarWidgetClassName) as HTMLCollectionOf<HTMLElement>;
      if (!!hotjarWidget && hotjarWidget.length > 0){
        hotjarWidget[0].hidden = shouldHideHotjar;
      }
      this.changeDetectorRef.detectChanges();
    }
  } 

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    //this.webjobHubConnection.off('WebJobCommunicateSystemIsOff');
    this.webjobHubConnection.stop();
    //this.agreementHubConnection.off('IntelligyOrdered');
    this.agreementHubConnection.stop();
  }

  onActivate(event: any) {
    if (isPlatformBrowser(this.platformId)) {
      let scrollToTop = window.setInterval(() => {
        let pos = window.pageYOffset;
        if (pos > 0) {
          window.scrollTo(0, pos - 50); // how far to scroll on each step
        } else {
          window.clearInterval(scrollToTop);
        }
      }, 16);
    }
  }

  closeMenu() {
    if (!this.sideNav) return;
    
    this.sideNav.toggle(false);
  }

  onToggleMenu(event: any) {
    this.sideNav.toggle();
  }

  private getUserInfoFromOidc = (data: any) : UserInfo => {
    if (data) {

      return {
        email: data.email,
        unValidatedEmail: data.unValidated_email,
        mobileNumber: data.phone_number,
        name: data.name,
        firstName: data.given_name,
        lastName: data.family_name,
        registerDate: data?.register_date,
        id: data.sub,
        impersonatedAsId: data['sx.impsub'],
        impersonatedAsName: data['sx.impname'],
        impersonatedAsFirstName: data['sx.impgiven_name'],
        impersonatedAsLastName: data['sx.impfamily_name'],
        impersonatedAsEmail: data['sx.impemail'],
        impersonatedAsMobileNumber: data['sx.impphone_number'],
        onBoardingComplete: data.onboarding_complete === 'True',
        autoRegCustomerId: data.autoReg_customerId,
        startupApplication: data.startup_application ? +data.startup_application : 1
      } as UserInfo;
    }
    return null;
  };

  private loadTimedData = () => {
    this.accountStore.dispatch(new fromAccountStore.NotificationLoadUnreadCount());
    this.accountStore.dispatch(new fromAccountStore.NotificationLoadCount());
    this.accountStore.dispatch(new fromAccountStore.NotificationLoad());
    this.accountStore.dispatch(new fromAccountStore.MessageLoadCount());
    this.accountStore.dispatch(new fromAccountStore.HasUserInfoExpired());
    // let deeplink = this.sxCookieService.getCookie('SX_DEEPLINK');

    // // Only use deeplink if onbaording is completed
    // if (deeplink && this.isOnboardingComplete) {
    //    console.log('deeplink: ' + deeplink + ' && this.isOnboardingComplete: ' + this.isOnboardingComplete );
    //   this.sxCookieService.clearCookie('SX_DEEPLINK');

    //   console.log('noCustomerRegistered: ' + this.noCustomerRegistered );
    //   if (!this.noCustomerRegistered) {
    //     // Only navigate to deeplink-url if user has customers
       
    //     this.router.navigate([deeplink]);
    //   }
    // }
  };

  private setupSignalR() {
    this.webjobHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.originUrl}/sxwebjobcommunicator`)
      .withAutomaticReconnect()
      .build();

    if (this.webjobHubConnection.state === signalR.HubConnectionState.Disconnected) {
      this.webjobHubConnection
        .start()
        .then(() => {
          let id = this.webjobHubConnection.connectionId;
        })
        .catch((err) => this.sxErrorService.logError(err));

      // Lås loginsidan via SignalR när systemet är nere, sker via EpiWorker - SendSystemIsOffmessage
      this.webjobHubConnection.on('WebJobCommunicateSystemIsOff', () => {
        if (!this.isImpersonating) {
          this.accountStore.dispatch(new fromAccountStore.UserLoggingOut());
        }
      });
    }
    /*
    -- Värmeoptimering start går inte längre att beställa
    this.agreementHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.originUrl}/agreementhub`)
      .withAutomaticReconnect()
      .build();

    if (this.agreementHubConnection.state === signalR.HubConnectionState.Disconnected) {
      this.agreementHubConnection.start().catch((err) => this.sxErrorService.logError(err));
    }

    this.agreementHubConnection.on('IntelligyOrdered', meteringPointId => {

      this.subs.sink = this.meteringPoints$.pipe(
        filter(mps => !!mps.meteringPoints && mps.meteringPoints.length > 0),
        take(1),
        map(mps => mps.meteringPoints.map(mp => mp.id)),
      ).subscribe(meteringPointIds =>{
        if (meteringPointIds.some(mpId => mpId === meteringPointId)) {
          this.meteringPointStore.dispatch(new fromMeteringStore.LoadAnyMeteringPointIntelligyReady(meteringPointIds));
        }
      })
    });
    */
  }
}
